'use client'

import { useEffect, useRef, useCallback } from 'react'
import {
  createChart,
  CandlestickSeries,
  AreaSeries,
  LineSeries,
  HistogramSeries,
  ColorType,
} from 'lightweight-charts'
import type { IChartApi, ISeriesApi, DeepPartial, ChartOptions, CandlestickData, SingleValueData, Time } from 'lightweight-charts'

/* ───── Tipler ───── */

export type ChartType = 'candlestick' | 'area' | 'line'

export interface OhlcvDataPoint {
  time: string
  open: number
  high: number
  low: number
  close: number
  volume: number | null
}

// Eski format — geriye uyumluluk
export interface ChartDataPoint {
  time: number
  value: number
}

export interface OhlcvCrosshair {
  time: string
  open: number
  high: number
  low: number
  close: number
  volume: number | null
}

interface Props {
  data: OhlcvDataPoint[] | ChartDataPoint[]
  isDark: boolean
  height?: number
  chartType?: ChartType
  showVolume?: boolean
  onCrosshairMove?: (point: OhlcvCrosshair | null) => void
  priceFormatter?: (price: number) => string
}

/* ───── Yardımcılar ───── */

function isOhlcv(data: unknown[]): data is OhlcvDataPoint[] {
  if (data.length === 0) return false
  const d = data[0] as Record<string, unknown>
  return 'open' in d && 'high' in d && 'low' in d && 'close' in d
}

/* ───── Bileşen ───── */

export default function FinancialChart({
  data,
  isDark,
  height = 480,
  chartType = 'candlestick',
  showVolume = true,
  onCrosshairMove,
  priceFormatter,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const priceSeriesRef = useRef<ISeriesApi<'Candlestick'> | ISeriesApi<'Area'> | ISeriesApi<'Line'> | null>(null)
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
      priceSeriesRef.current = null
      volumeSeriesRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return

    destroyChart()

    const ohlcvMode = isOhlcv(data)
    const ohlcvData = ohlcvMode ? (data as OhlcvDataPoint[]) : null
    const simpleData = !ohlcvMode ? (data as ChartDataPoint[]) : null

    // Yön tespiti
    const firstClose = ohlcvMode ? ohlcvData![0].close : simpleData![0].value
    const lastClose = ohlcvMode ? ohlcvData![ohlcvData!.length - 1].close : simpleData![simpleData!.length - 1].value
    const isUp = lastClose >= firstClose

    // Renk paleti
    const upColor = isDark ? '#66BB6A' : '#4CAF50'
    const downColor = isDark ? '#EF5350' : '#E53935'
    const lineColor = isUp ? upColor : downColor

    const options: DeepPartial<ChartOptions> = {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: isDark ? '#94A3B8' : '#64748B',
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: isDark ? '#1e293b' : '#f1f5f9' },
        horzLines: { color: isDark ? '#1e293b' : '#f1f5f9' },
      },
      width: containerRef.current.clientWidth,
      height,
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: showVolume && ohlcvMode ? { top: 0.05, bottom: 0.22 } : { top: 0.05, bottom: 0.05 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        secondsVisible: false,
      },
      crosshair: {
        vertLine: { color: isDark ? '#475569' : '#cbd5e1', width: 1, style: 3 },
        horzLine: { color: isDark ? '#475569' : '#cbd5e1', width: 1, style: 3 },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true },
    }

    const chart = createChart(containerRef.current, options)
    chartRef.current = chart

    // TradingView logosunu kaldır
    const tvLink = containerRef.current.querySelector('a[href*="tradingview"]')
    if (tvLink) (tvLink as HTMLElement).style.display = 'none'

    // Aktif chart type'a göre series oluştur
    const effectiveType = (ohlcvMode && chartType === 'candlestick') ? 'candlestick'
      : (chartType === 'line') ? 'line' : 'area'

    const priceFmt: any = priceFormatter ? { type: 'custom', formatter: priceFormatter, minMove: 0.01 } : undefined

    if (effectiveType === 'candlestick' && ohlcvMode) {
      const series = chart.addSeries(CandlestickSeries, {
        upColor,
        downColor,
        borderUpColor: upColor,
        borderDownColor: downColor,
        wickUpColor: upColor,
        wickDownColor: downColor,
        ...(priceFmt && { priceFormat: priceFmt }),
      })
      series.setData(ohlcvData!.map(d => ({
        time: d.time as Time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      })))
      priceSeriesRef.current = series
    } else if (effectiveType === 'line') {
      const series = chart.addSeries(LineSeries, {
        color: lineColor,
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        crosshairMarkerBorderColor: lineColor,
        crosshairMarkerBackgroundColor: isDark ? '#1e293b' : '#ffffff',
        ...(priceFmt && { priceFormat: priceFmt }),
      })
      const lineData = ohlcvMode
        ? ohlcvData!.map(d => ({ time: d.time as Time, value: d.close }))
        : simpleData!.map(d => ({ time: (d.time + 3 * 3600) as Time, value: d.value }))
      series.setData(lineData)
      priceSeriesRef.current = series
    } else {
      // Area
      const topColor = isUp ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'
      const bottomColor = isUp ? 'rgba(34,197,94,0.0)' : 'rgba(239,68,68,0.0)'
      const series = chart.addSeries(AreaSeries, {
        topColor,
        bottomColor,
        lineColor,
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        crosshairMarkerBorderColor: lineColor,
        crosshairMarkerBackgroundColor: isDark ? '#1e293b' : '#ffffff',
        ...(priceFmt && { priceFormat: priceFmt }),
      })
      const areaData = ohlcvMode
        ? ohlcvData!.map(d => ({ time: d.time as Time, value: d.close }))
        : simpleData!.map(d => ({ time: (d.time + 3 * 3600) as Time, value: d.value }))
      series.setData(areaData)
      priceSeriesRef.current = series
    }

    // Volume histogram
    if (showVolume && ohlcvMode) {
      const volSeries = chart.addSeries(HistogramSeries, {
        priceScaleId: 'volume',
        priceFormat: { type: 'volume' },
      })
      chart.priceScale('volume').applyOptions({
        scaleMargins: { top: 0.82, bottom: 0 },
        borderVisible: false,
      })
      volSeries.setData(ohlcvData!.map(d => ({
        time: d.time as Time,
        value: d.volume ?? 0,
        color: d.close >= d.open
          ? (isDark ? 'rgba(102,187,106,0.35)' : 'rgba(76,175,80,0.35)')
          : (isDark ? 'rgba(239,83,80,0.35)' : 'rgba(229,57,53,0.35)'),
      })))
      volumeSeriesRef.current = volSeries
    }

    // Crosshair callback
    if (onCrosshairMove && ohlcvMode) {
      chart.subscribeCrosshairMove(param => {
        if (!param.time || !param.seriesData || param.seriesData.size === 0) {
          onCrosshairMove(null)
          return
        }
        const priceData = param.seriesData.get(priceSeriesRef.current!)
        if (!priceData) { onCrosshairMove(null); return }

        let o: number, h: number, l: number, c: number
        if ('open' in priceData) {
          const cd = priceData as CandlestickData<Time>
          o = cd.open; h = cd.high; l = cd.low; c = cd.close
        } else {
          const sv = priceData as SingleValueData<Time>
          o = h = l = c = sv.value
        }

        const volData = volumeSeriesRef.current ? param.seriesData.get(volumeSeriesRef.current) : null
        const vol = volData && 'value' in volData ? (volData as SingleValueData<Time>).value : null

        onCrosshairMove({
          time: param.time as string,
          open: o, high: h, low: l, close: c,
          volume: vol,
        })
      })
    }

    chart.timeScale().fitContent()

    // Resize
    const ro = new ResizeObserver(entries => {
      if (chartRef.current && entries[0]) {
        chartRef.current.applyOptions({ width: entries[0].contentRect.width })
      }
    })
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      destroyChart()
    }
  }, [data, isDark, height, chartType, showVolume, onCrosshairMove, priceFormatter, destroyChart])

  return <div ref={containerRef} className="w-full" />
}
